import { Injectable } from '@angular/core';
import { ApiClient, BenutzerProfil, BenutzerProfilFavorit, Lieferant, MerkmalInfoResult, ObjektTyp } from 'app/core/services/api-client';
import { ApiErrorHandlerService } from 'app/core/services/api-default-error-handler/api-error-handler.service';
import { Observable, of as ObservableOf } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ClientInfo } from './models/clientInfo';
import { StoredFavoriteMerkmal } from '@models/dossier/dossier-models';
import * as _ from 'lodash';

@Injectable()
export class BenutzerprofilService {
  private MAX_FAVOURITE_MERKMALE = 15;

  public constructor(
    private apiClient: ApiClient,
    private errorHandler: ApiErrorHandlerService,
  ) {}

  public loadUserProfile(): Observable<BenutzerProfil> {
    return this.apiClient.apiBenutzerProfilGet().pipe(
      catchError(
        this.apiClient.apiBenutzerProfilGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public loadMeinLieferant(): Observable<Lieferant> {
    return this.apiClient.apiMeinLieferantGet().pipe(
      catchError(
        this.apiClient.apiMeinLieferantGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
          error404Handler: error404 =>
            this.errorHandler.handle404Error(error404, {
              messageKey: 'errors.404.apiMeinLieferantGet', // Lieferant existiert nicht
            }),
        }),
      ),
    );
  }

  public checkFavorit(objektTyp: ObjektTyp, objektId: number): Observable<BenutzerProfilFavorit[]> {
    return this.apiClient.apiBenutzerProfilFavoritenGet(objektTyp, objektId).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public loadFavoriten(): Observable<BenutzerProfilFavorit[]> {
    return this.apiClient.apiBenutzerProfilFavoritenGet().pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenGetErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public addFavorit(objektTyp: ObjektTyp, objektId: number): Observable<BenutzerProfilFavorit> {
    return this.apiClient.apiBenutzerProfilFavoritenPost(new BenutzerProfilFavorit({ objektId: objektId, objektTyp: objektTyp })).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenPostErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public removeFavorit(objektTyp: ObjektTyp, objektId: number): Observable<void> {
    return this.apiClient.apiBenutzerProfilFavoritenByTypByIdPost(objektTyp, objektId).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilFavoritenByTypByIdPostErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }

  public getMerkmalInfo(keys: string[]): Observable<MerkmalInfoResult[]> {
    return this.apiClient.apiDatenmodellMerkmalInfoPost(keys);
  }

  public addFavouriteMerkmal(key: string): Observable<StoredFavoriteMerkmal[]> {
    const currentState: StoredFavoriteMerkmal[] = JSON.parse(localStorage.getItem('favouriteMerkmale')) || [];
    return this.getMerkmalInfo([key]).pipe(
      switchMap(merkmalInfos => {
        const info = merkmalInfos && merkmalInfos.find(i => i.name === key);
        if (!info || !info.displayName) {
          return ObservableOf(currentState);
        }
        const updated = [
          { key, displayName: info.displayName, timestamp: new Date() },
          ...currentState.filter(m => m.key !== key),
        ].slice(0, this.MAX_FAVOURITE_MERKMALE);
        localStorage.setItem('favouriteMerkmale', JSON.stringify(updated));
        return ObservableOf(updated);
      }),
      catchError(() => ObservableOf(currentState)),
    );
  }

  public saveClientInfo(clientInfo: ClientInfo): Observable<void> {
    return this.apiClient.apiBenutzerProfilClientStatePut(clientInfo).pipe(
      catchError(
        this.apiClient.apiBenutzerProfilClientStatePutErrorHandlers({
          error401Handler: error401 => this.errorHandler.handle401Error(error401),
        }),
      ),
    );
  }
}
